import { useState, useEffect } from 'react';
import { collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { AuditRun, AIResponse } from '../../types';
import { useAuth } from '../../hooks/useAuth';
import { Download, Loader2, ChevronDown, ChevronUp, Filter, X, Search, Bell } from 'lucide-react';
import { Badge, EmptyState } from '../shared';

/**
 * Audit Explorer per UX Spec page 8:
 * - Filter row (system/category/flag/date) as chips
 * - Response cards with AI system badge, prompt, excerpt, claims, severity, score
 * - Expand on click for full response detail
 * - CSV export functionality
 */

export function AuditExplorer() {
  const { user } = useAuth();
  const [auditRuns, setAuditRuns] = useState<AuditRun[]>([]);
  const [selectedRun, setSelectedRun] = useState<string | null>(null);
  const [responses, setResponses] = useState<AIResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingResponses, setLoadingResponses] = useState(false);
  const [expandedResponse, setExpandedResponse] = useState<string | null>(null);
  const [filterSystem, setFilterSystem] = useState<string | null>(null);
  const [filterSeverity, setFilterSeverity] = useState<string | null>(null);

  useEffect(() => {
    loadAuditRuns();
  }, [user]);

  const loadAuditRuns = async () => {
    if (!user) return;

    try {
      const q = query(
        collection(db, 'auditRuns'),
        where('userId', '==', user.uid),
        orderBy('startedAt', 'desc')
      );
      const snapshot = await getDocs(q);
      setAuditRuns(snapshot.docs.map((doc) => doc.data() as AuditRun));
    } catch (error) {
      console.error('Error loading audit runs:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadResponses = async (runId: string) => {
    setLoadingResponses(true);
    try {
      const q = query(
        collection(db, 'aiResponses'),
        where('auditRunId', '==', runId)
      );
      const snapshot = await getDocs(q);
      setResponses(snapshot.docs.map((doc) => doc.data() as AIResponse));
    } catch (error) {
      console.error('Error loading responses:', error);
    } finally {
      setLoadingResponses(false);
    }
  };

  const handleSelectRun = (runId: string) => {
    if (selectedRun === runId) {
      setSelectedRun(null);
      setResponses([]);
    } else {
      setSelectedRun(runId);
      loadResponses(runId);
    }
  };

  const exportToCSV = () => {
    if (responses.length === 0) return;

    const headers = ['AI System', 'Prompt', 'Response', 'Mentioned', 'Recommended', 'Sentiment', 'Sentiment Score', 'Claims'];
    const rows = responses.map((r) => [
      r.aiSystem,
      r.promptText,
      r.rawResponse,
      r.mentioned ? 'Yes' : 'No',
      r.recommendationContext ? 'Yes' : 'No',
      r.sentiment,
      r.sentimentScore.toString(),
      r.extractedClaims?.length.toString() || '0',
    ]);

    const csv = [headers, ...rows].map((row) => row.map((cell) => `"${cell}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `audit-responses-${selectedRun}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const filteredResponses = responses.filter((r) => {
    if (filterSystem && r.aiSystem !== filterSystem) return false;
    if (filterSeverity === 'critical' && r.sentiment !== 'negative') return false;
    if (filterSeverity === 'warning' && r.sentiment !== 'neutral') return false;
    if (filterSeverity === 'success' && r.sentiment !== 'positive') return false;
    return true;
  });

  const uniqueSystems = Array.from(new Set(responses.map((r) => r.aiSystem)));

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-t1-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Top Navigation Bar */}
      <div className="bg-shell-navy border-b border-gray-700 px-6 py-3 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <span className="text-gray-400 text-sm">Audit</span>
          <span className="text-gray-600">/</span>
          <span className="text-white text-sm font-medium">Audit Explorer</span>
        </div>

        <div className="flex items-center gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-500" />
            <input
              type="text"
              placeholder="Search responses..."
              className="pl-10 pr-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm text-white placeholder-gray-500 w-64 focus:outline-none focus:border-t1-primary"
            />
          </div>
          <span className="px-3 py-1 bg-orange-600 text-white text-xs font-semibold rounded uppercase">Growth</span>
          <button className="relative p-2 text-gray-400 hover:text-white">
            <Bell className="w-5 h-5" />
            <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"></span>
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="px-6 py-8">
        <div className="mb-6">
          <p className="text-xs font-semibold text-t1-primary uppercase tracking-wide mb-1">Kodiac Audit</p>
          <h1 className="text-3xl font-semibold text-gray-900">Audit Explorer</h1>
          <p className="text-sm text-gray-600 mt-1">Explore AI responses across all platforms and audit runs</p>
        </div>

        {/* Action Bar */}
        {selectedRun && responses.length > 0 && (
          <div className="flex items-center justify-end mb-6">
            <button
              onClick={exportToCSV}
              className="flex items-center gap-2 px-4 py-2 bg-t1-primary text-white rounded-lg hover:bg-t1-dark"
            >
              <Download className="w-4 h-4" />
              Export CSV
            </button>
          </div>
        )}

      {auditRuns.length === 0 ? (
        <EmptyState
          icon={Filter}
          title="No audits yet"
          description="Run your first audit from the dashboard to start tracking AI responses"
        />
      ) : (
        <div className="space-y-4">
          {auditRuns.map((run) => (
            <div key={run.id} className="bg-white rounded-card border border-gray-200 overflow-hidden">
              {/* Audit Run Header */}
              <button
                onClick={() => handleSelectRun(run.id)}
                className="w-full p-4 flex items-center justify-between hover:bg-surface-warm transition-colors text-left"
              >
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="text-heading text-gray-900">
                      {run.startedAt.toDate().toLocaleDateString()} at {run.startedAt.toDate().toLocaleTimeString()}
                    </h3>
                    {run.status === 'completed' && <Badge variant="success">Completed</Badge>}
                    {run.status === 'failed' && <Badge variant="critical">Failed</Badge>}
                    {run.status === 'running' && <Badge variant="primary">Running</Badge>}
                    {run.isTestData && <Badge variant="warning">Test Data</Badge>}
                  </div>
                  <div className="flex items-center gap-4 text-caption text-gray-600">
                    <span>{run.completedQueries} / {run.totalQueries} queries</span>
                    <span>Score: {run.visibilityScore}</span>
                  </div>
                </div>
                {selectedRun === run.id ? (
                  <ChevronUp className="w-5 h-5 text-gray-400" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-gray-400" />
                )}
              </button>

              {/* Responses List */}
              {selectedRun === run.id && (
                <div className="border-t border-gray-200 bg-surface-warm">
                  {loadingResponses ? (
                    <div className="p-12 flex items-center justify-center">
                      <Loader2 className="w-6 h-6 animate-spin text-t1-primary" />
                    </div>
                  ) : responses.length === 0 ? (
                    <div className="p-12">
                      <EmptyState
                        icon={Filter}
                        title="No responses"
                        description="This audit run has no recorded responses"
                      />
                    </div>
                  ) : (
                    <div className="p-4 space-y-4">
                      {/* Filter Row - Per Spec Page 8 */}
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-label text-gray-700">Filter:</span>
                        {uniqueSystems.map((system) => (
                          <button
                            key={system}
                            onClick={() => setFilterSystem(filterSystem === system ? null : system)}
                            className={`px-2 py-1 rounded-chip text-caption font-medium transition-colors ${
                              filterSystem === system
                                ? 'bg-t1-primary text-white'
                                : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                            }`}
                          >
                            {system}
                            {filterSystem === system && <X className="w-3 h-3 inline ml-1" />}
                          </button>
                        ))}
                        {['critical', 'warning', 'success'].map((severity) => (
                          <button
                            key={severity}
                            onClick={() => setFilterSeverity(filterSeverity === severity ? null : severity)}
                            className={`px-2 py-1 rounded-chip text-caption font-medium transition-colors ${
                              filterSeverity === severity
                                ? 'bg-t1-primary text-white'
                                : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                            }`}
                          >
                            {severity.charAt(0).toUpperCase() + severity.slice(1)}
                            {filterSeverity === severity && <X className="w-3 h-3 inline ml-1" />}
                          </button>
                        ))}
                        {(filterSystem || filterSeverity) && (
                          <button
                            onClick={() => {
                              setFilterSystem(null);
                              setFilterSeverity(null);
                            }}
                            className="text-caption text-gray-600 hover:text-gray-900"
                          >
                            Clear all
                          </button>
                        )}
                      </div>

                      {/* Response Count */}
                      <p className="text-label text-gray-700">
                        {filteredResponses.length} of {responses.length} responses
                      </p>

                      {/* Response Cards - Per Spec Page 8 */}
                      {filteredResponses.map((response) => {
                        // Severity border based on sentiment
                        const severityBorder =
                          response.sentiment === 'positive'
                            ? 'border-l-success-green'
                            : response.sentiment === 'negative'
                            ? 'border-l-critical-red'
                            : 'border-l-warning-amber';

                        return (
                        <div
                          key={response.id}
                          className={`bg-white rounded-card border border-gray-200 border-l-[6px] ${severityBorder} overflow-hidden`}
                        >
                          <button
                            onClick={() =>
                              setExpandedResponse(
                                expandedResponse === response.id ? null : response.id
                              )
                            }
                            className="w-full p-3 flex items-start gap-3 hover:bg-surface-warm transition-colors text-left"
                          >
                            <div className="flex-1 min-w-0">
                              {/* AI System + Status Badges */}
                              <div className="flex items-center gap-2 mb-2 flex-wrap">
                                <Badge variant="primary">{response.aiSystem}</Badge>
                                {response.mentioned && <Badge variant="success">Mentioned</Badge>}
                                {response.recommendationContext && <Badge variant="accent">Recommended</Badge>}
                                <Badge
                                  variant={
                                    response.sentiment === 'positive'
                                      ? 'success'
                                      : response.sentiment === 'negative'
                                      ? 'critical'
                                      : 'warning'
                                  }
                                >
                                  {response.sentiment}
                                </Badge>
                              </div>

                              {/* Prompt */}
                              <p className="text-body text-gray-900 font-medium mb-1 line-clamp-1">
                                {response.promptText}
                              </p>

                              {/* Response Excerpt */}
                              <p className="text-caption text-gray-600 line-clamp-2 mb-2">
                                {response.rawResponse}
                              </p>

                              {/* Claims Count */}
                              {response.extractedClaims && response.extractedClaims.length > 0 && (
                                <p className="text-caption text-gray-500">
                                  {response.extractedClaims.length} claim{response.extractedClaims.length !== 1 ? 's' : ''}
                                </p>
                              )}
                            </div>

                            {/* Score + Expand Icon */}
                            <div className="flex items-start gap-3 flex-shrink-0">
                              <div className="text-center">
                                <div className="text-lg font-medium text-t1-primary">
                                  {response.sentimentScore}
                                </div>
                                <div className="text-caption text-gray-500">score</div>
                              </div>
                              {expandedResponse === response.id ? (
                                <ChevronUp className="w-5 h-5 text-gray-400 mt-1" />
                              ) : (
                                <ChevronDown className="w-5 h-5 text-gray-400 mt-1" />
                              )}
                            </div>
                          </button>

                          {expandedResponse === response.id && (
                            <div className="border-t border-gray-200 p-3 space-y-3 bg-surface-warm">
                              <div>
                                <p className="text-label text-gray-700 mb-1">PROMPT</p>
                                <p className="text-body text-gray-900 bg-white p-3 rounded-card border border-gray-200">
                                  {response.promptText}
                                </p>
                              </div>
                              <div>
                                <p className="text-label text-gray-700 mb-1">RESPONSE</p>
                                <p className="text-body text-gray-900 bg-white p-3 rounded-card border border-gray-200 whitespace-pre-wrap">
                                  {response.rawResponse}
                                </p>
                              </div>
                              {response.extractedClaims && response.extractedClaims.length > 0 && (
                                <div>
                                  <p className="text-label text-gray-700 mb-1">
                                    EXTRACTED CLAIMS ({response.extractedClaims.length})
                                  </p>
                                  <div className="space-y-2">
                                    {response.extractedClaims.map((claim, idx) => (
                                      <div
                                        key={idx}
                                        className="bg-white p-3 rounded-card border border-gray-200"
                                      >
                                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                                          <Badge variant="primary">{claim.type}</Badge>
                                          {claim.isAccurate === false && (
                                            <Badge variant="critical">Inaccurate</Badge>
                                          )}
                                          {claim.isAccurate === true && (
                                            <Badge variant="success">Accurate</Badge>
                                          )}
                                        </div>
                                        <p className="text-body text-gray-900 mt-1">{claim.text}</p>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      </div>
    </div>
  );
}
